import React from "react";
import {
    loadHistoryFromLocalStorage,
    saveHistoryToLocalStorage,
} from "../../lib/local-storage";
import {
    computeStreak,
    computeStreaksBySport,
    computeBestStreakBySport,
    normalizeHistory,
    saveProgressToSupabase,
    loadUserProgressFromSupabase,
} from "../../lib/progress-service";
import { getSupabaseClient } from "../../lib/supabaseClient";
import { AuthContext } from "../Auth";

export const ProgressContext = React.createContext({
    history: [],
    streak: 0,
    stats: {},
    recordResult: () => {},
    refreshHistory: () => {},
});


function ProgressProvider({ children }) {
    const supabase = getSupabaseClient();
    const { user } = React.useContext(AuthContext);
    const [history, setHistory] = React.useState(() =>
        normalizeHistory(loadHistoryFromLocalStorage(user?.id))
    );

    const refreshHistory = React.useCallback(async () => {
        const localHistory = normalizeHistory(loadHistoryFromLocalStorage(user?.id));
        setHistory(localHistory);

        if (user && supabase) {
            // Use the new user_puzzle_completions table
            const { data, error } = await loadUserProgressFromSupabase(user);
            if (!error && Array.isArray(data)) {
                setHistory(normalizeHistory(data));
            }
        }
    }, [user, supabase]);

    React.useEffect(() => {
        refreshHistory();
    }, [refreshHistory]);

    const recordResult = React.useCallback(
        async ({ puzzleId, puzzleDate, sport, result, mistakes, guesses }) => {
            if (!puzzleId || !puzzleDate) {
                return;
            }
            const entry = {
                puzzleId,
                puzzleDate,
                sport,
                result,
                mistakes,
                guesses,
                completedAt: new Date().toISOString(),
            };

            setHistory((prev) => {
                const filtered = prev.filter((h) => h.puzzleId !== puzzleId);
                const updated = normalizeHistory([entry, ...filtered]);
                saveHistoryToLocalStorage(user?.id, updated);
                return updated;
            });

            if (user) {
                await saveProgressToSupabase(user, entry);
            }
        },
        [user]
    );

    const streak = computeStreak(history);
    const stats = React.useMemo(() => {
        const wins = history.filter((h) => h.result === "win").length;
        const losses = history.filter((h) => h.result === "loss").length;
        const total = history.length;

        // Compute sport-specific streaks
        const streaks = computeStreaksBySport(history);
        const bestStreaks = {
            nfl: computeBestStreakBySport(history, "nfl"),
            nba: computeBestStreakBySport(history, "nba"),
            mlb: computeBestStreakBySport(history, "mlb"),
        };

        // Compute sport-specific stats (wins, total, win rate)
        const computeSportStats = (sport) => {
            const sportHistory = history.filter((h) => h.sport === sport);
            const sportWins = sportHistory.filter((h) => h.result === "win").length;
            const sportTotal = sportHistory.length;
            return {
                wins: sportWins,
                total: sportTotal,
                winRate: sportTotal ? Math.round((sportWins / sportTotal) * 100) : 0,
            };
        };

        const bySport = {
            nfl: computeSportStats("nfl"),
            nba: computeSportStats("nba"),
            mlb: computeSportStats("mlb"),
        };

        return {
            total,
            wins,
            losses,
            winRate: total ? Math.round((wins / total) * 100) : 0,
            bestStreak: Math.max(bestStreaks.nfl, bestStreaks.nba, bestStreaks.mlb),
            currentStreak: streak,
            // Sport-specific data
            streaks,
            bestStreaks,
            bySport,
            lastPuzzle: history[0],
        };
    }, [history, streak]);

    return (
        <ProgressContext.Provider
            value={{
                history,
                streak,
                stats,
                recordResult,
                refreshHistory,
            }}
        >
            {children}
        </ProgressContext.Provider>
    );
}

export default ProgressProvider;
